import { generateSignature } from '@/lib/getSignature';
import { transformImageUrl } from '@/lib/transformImageUrl';
import { trimPresignedUrl } from '@/lib/trimPresignedUrl';
import { getTimeZone } from '@/lib/utils';
import { authStorage } from '@/services/storage/AuthStorage';
import { InternalAxiosRequestConfig } from 'axios';
import { getApiLocale } from '../apiLocale';
import { getOrCreateRefreshPromise, logoutAndRedirect } from './responseInterceptors';

export const requestSuccessInterceptor = async (
  config: InternalAxiosRequestConfig,
): Promise<InternalAxiosRequestConfig> => {
  config.headers = config.headers || {};
  config.headers['Accept-Language'] = getApiLocale();

  if (config.data && ['post', 'put', 'patch', 'delete'].includes(config.method?.toLowerCase() ?? '')) {
    config.data = transformImageUrl(config.data);
  }

  // Generate signature
  const isPresignedUrl = config.url && trimPresignedUrl(config.url).includes('presigned-url');

  if (isPresignedUrl) {
    // Remove all sensitive headers
    config.headers = config.headers || {};
    delete config.headers['Authorization'];
    delete config.headers['Signature'];
    delete config.headers['Action-Platform'];
    delete config.headers['x-timezone-offset'];

    // Do NOT add signature or token logic
    return config;
  }

  const secretKey = (import.meta.env.VITE_SECRET_KEY as string) || '';
  let payload: Record<string, unknown> = {};

  if (config.data && typeof config.data === 'object' && !(config.data instanceof FormData)) {
    payload = { ...(config.data as Record<string, unknown>) };
  } else if (typeof config.data === 'string') {
    try {
      const parsed = JSON.parse(config.data) as unknown;
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        payload = parsed as Record<string, unknown>;
      } else {
        payload = { data: config.data };
      }
    } catch {
      payload = { data: config.data };
    }
  }

  const signature = generateSignature(payload, secretKey);
  const token = authStorage.accessToken;
  const refreshToken = authStorage.refreshToken;
  const expires = authStorage.expires;
  const now = Date.now();

  // Preflight refresh when access token is missing/expired and refresh token exists.
  if (config.url && !config.url.includes('/refresh-token')) {
    const shouldRefresh = Boolean(refreshToken) && (!token || !expires || expires * 1000 <= now);
    if (shouldRefresh) {
      try {
        const refreshed = await getOrCreateRefreshPromise();
        config.headers = config.headers || {};
        config.headers['Authorization'] = `Bearer ${refreshed.accessToken}`;
      } catch (error) {
        logoutAndRedirect();
        throw error;
      }
    } else if (token) {
      config.headers = config.headers || {};
      config.headers['Authorization'] = `Bearer ${token}`;
    }
  }

  // ✅ Attach signature + actionPlatform in headers
  const timeZone = getTimeZone();
  config.headers = config.headers || {};
  config.headers['Signature'] = signature;
  config.headers['Action-Platform'] = 'web';
  config.headers['x-timezone-offset'] = timeZone;

  return config;
};
